import {
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  CodeChip,
  KeyValue,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from '@provenance/design';
import { DocumentStatus, UserRole, type ChamberIssuePreferences, type DocumentDetail } from '@provenance/shared';
import { useSession } from '../lib/session';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  Ban,
  Check,
  Download,
  FileClock,
  FileText,
  Loader2,
  MessageSquareWarning,
  Printer,
  RefreshCw,
  ScrollText,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApproveDialog } from '../features/documents/ApproveDialog';
import { CheckPanel } from '../features/documents/CheckPanel';
import { ClarificationDialog, RejectDialog, RevokeDialog } from '../features/documents/DecisionDialogs';
import { PdfViewer } from '../features/documents/PdfViewer';
import { RaiseModificationDialog } from './Modifications';
import { ShipmentPanel } from '../features/documents/ShipmentPanel';
import { AttachmentsPanel } from '../features/documents/AttachmentsPanel';
import { SendAgain } from '../features/documents/SendAgain';
import { ApiError, api } from '../lib/api';
import { auCalendarDate, auDate, auDateTime, country, relative } from '../lib/format';
import { DocumentStatusBadge } from '../lib/status';
import { IntakeChannelBadge } from '../features/documents/IntakeChannelBadge';
import { ExtractionPanel } from '../features/documents/ExtractionPanel';
import { SignatureComparison } from '../features/documents/SignatureComparison';

/**
 * The document review screen.
 *
 * Where the actual judgement happens, so the layout is the design: the PDF on the left,
 * the submitted data and the completeness check on the right, in one visual field. An
 * operator who has to switch tabs between the document and the data will approve without
 * reading one of them.
 *
 * The whole screen comes from ONE request (`GET /documents/:id`), including the full
 * Approve-dialog context. Four sequential spinners before an operator can start reading
 * is a worse experience than one slightly larger payload.
 */
export function DocumentReview() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [clarifyOpen, setClarifyOpen] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const { user } = useSession();
  const [modifyOpen, setModifyOpen] = useState(false);

  const query = useQuery({
    queryKey: ['document', id],
    queryFn: () => api.get<DocumentDetail>(`/api/console/documents/${id}`),
    enabled: Boolean(id),
    // The completeness check runs as a job when the document is opened, so the panel
    // needs to pick up the result without the operator refreshing. Polls only while it
    // is actually pending.
    refetchInterval: (query) => {
      const data = query.state.data;
      const checking = data?.checks.state === 'QUEUED' || data?.checks.state === 'RUNNING';
      // Approved, no failure recorded, and no signed PDF yet: stamping is in flight.
      //
      // This used to test `!certificateNo`, which approval sets in the same transaction as
      // the status — so it was never true and the screen never polled. An operator watched
      // "still being generated" until they refreshed the page by hand.
      const stamping =
        data?.status === DocumentStatus.APPROVED &&
        !data?.stamping &&
        !data?.documentFileUrl?.includes('/signed/');
      return checking || stamping ? 2000 : false;
    },
  });

  /** Applying the model's suggested document type — never automatic (§26.3). */
  const changeType = useMutation({
    mutationFn: (documentTypeId: string) =>
      api.patch(`/api/console/documents/${id}/document-type`, { documentTypeId }),
    onSuccess: () => {
      toast.success('Document type changed', 'Re-checking against the new type.');
      void queryClient.invalidateQueries({ queryKey: ['document', id] });
    },
    onError: (error) =>
      toast.error(
        'Could not change the document type',
        error instanceof ApiError ? error.message : 'Something went wrong.',
      ),
  });

  const recheck = useMutation({
    mutationFn: () => api.post(`/api/console/documents/${id}/recheck`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['document', id] }),
  });

  /**
   * Printing the certificate.
   *
   * The PDF is fetched into a blob and printed from a hidden same-origin iframe. Pointing
   * an iframe straight at the storage URL and calling `print()` on it is blocked as a
   * cross-origin frame, so the print silently never happens.
   */
  const [printing, setPrinting] = useState(false);
  const printFrame = useRef<HTMLIFrameElement | null>(null);

  const print = async (url: string) => {
    setPrinting(true);
    try {
      const blob = await (await fetch(url)).blob();
      const objectUrl = URL.createObjectURL(blob);

      const frame = document.createElement('iframe');
      frame.style.position = 'fixed';
      frame.style.right = '0';
      frame.style.bottom = '0';
      frame.style.width = '0';
      frame.style.height = '0';
      frame.style.border = '0';
      frame.src = objectUrl;
      frame.onload = () => {
        try {
          frame.contentWindow?.focus();
          frame.contentWindow?.print();
        } catch {
          // Some browsers refuse to print an embedded PDF. Opening it is the honest
          // fallback — the operator still gets to the document in one step.
          window.open(objectUrl, '_blank', 'noopener');
        }
      };
      // Replaces any previous frame, so printing twice does not leak two PDFs.
      printFrame.current?.remove();
      printFrame.current = frame;
      document.body.appendChild(frame);
    } catch {
      toast.error('Could not print', 'Open the certificate and print it from there.');
    } finally {
      setPrinting(false);
    }
  };

  useEffect(() => () => printFrame.current?.remove(), []);

  /** Off unless the chamber has asked for it. Read fresh, not carried on the session. */
  const preferences = useQuery({
    queryKey: ['issue-preferences'],
    queryFn: () => api.get<ChamberIssuePreferences>('/api/console/configuration/issue-preferences'),
    staleTime: 60_000,
  });

  /**
   * Print the moment stamping finishes, when the chamber has asked for that.
   *
   * Fires on the TRANSITION, so revisiting an already-issued certificate does not reprint
   * it — which would be the fastest way to make a chamber turn the setting back off.
   */
  const wasAwaitingStamp = useRef(false);
  const signedUrl =
    query.data?.status === DocumentStatus.APPROVED && query.data.documentFileUrl?.includes('/signed/')
      ? query.data.documentFileUrl
      : null;

  useEffect(() => {
    const awaiting =
      query.data?.status === DocumentStatus.APPROVED && !signedUrl && !query.data?.stamping;
    if (wasAwaitingStamp.current && signedUrl) {
      toast.success('Certificate stamped', `${query.data?.certificateNo ?? 'It'} is ready.`);
      if (preferences.data?.printCertificateOnIssue) void print(signedUrl);
    }
    wasAwaitingStamp.current = awaiting;
    // `print` and `toast` are stable enough for this transition check; re-running on their
    // identity would fire the toast again on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedUrl, query.data?.status, query.data?.stamping, preferences.data?.printCertificateOnIssue]);

  const retryStamping = useMutation({
    mutationFn: () => api.post(`/api/console/documents/${id}/retry-stamping`),
    onSuccess: () => {
      toast.success('Stamping restarted', 'The certificate is being generated again.');
      void queryClient.invalidateQueries({ queryKey: ['document', id] });
    },
    onError: (error) =>
      toast.error('Could not restart stamping', error instanceof ApiError ? error.message : ''),
  });

  if (query.isLoading) {
    return (
      <div className="review-grid">
        <Skeleton className="h-[40rem]" />
        <div className="flex flex-col gap-4">
          <Skeleton className="h-40" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Callout tone="stop" icon={<AlertTriangle className="size-4" />} title="Could not open this request">
        {query.error instanceof ApiError
          ? query.error.message
          : 'It may have been actioned by someone else, or it may not belong to your chamber.'}
        <div className="mt-3">
          <Button variant="secondary" size="sm" onClick={() => navigate('/documents/pending')}>
            Back to the queue
          </Button>
        </div>
      </Callout>
    );
  }

  const doc = query.data;
  const actionable = doc.status === DocumentStatus.AWAITING_ACTION;
  // Provenir Support may look at anything here, and decide nothing (D-031). The API refuses
  // these anyway; hiding them stops the screen offering a button that can only fail.
  const isSupport = user?.role === UserRole.PLATFORM_SUPPORT;
  const issued = doc.status === DocumentStatus.APPROVED;
  // Approved but the stamping job has not finished, or failed. Never hidden — a
  // certificate that exists without its stamped PDF is a state the product is honest
  // about (see StampingJobService).
  const awaitingStamp = issued && !doc.documentFileUrl?.includes('/signed/');

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start gap-4">
        <div className="min-w-0 flex-1">
          <Link
            to={
              actionable
                ? '/documents/pending'
                : doc.status === DocumentStatus.CLARIFICATION_REQUESTED
                  ? '/documents/clarification'
                  : '/documents/processed'
            }
            className="inline-flex items-center gap-1.5 [font-size:var(--pv-text-xs)] text-[var(--pv-muted)] hover:text-[var(--pv-ink)]"
          >
            <ArrowLeft aria-hidden className="size-3.5" /> Back to the queue
          </Link>

          <div className="mt-2 flex flex-wrap items-center gap-2.5">
            <h1 className="[font-size:var(--pv-text-h1)] font-bold tracking-[-0.02em]">
              {doc.organisationName}
            </h1>
            <DocumentStatusBadge status={doc.status} />
            {!doc.organisationId && (
              <Badge tone="warn" size="sm">
                Not a registered exporter
              </Badge>
            )}
            {doc.submissionType === 'AMENDMENT' && (
              <Badge tone="info" size="sm">
                Amendment of {doc.originalCertificateNo}
              </Badge>
            )}
            {doc.amendmentStanding && (
              <Badge
                tone={doc.amendmentStanding.ordinal > doc.amendmentStanding.limit ? 'warn' : 'neutral'}
                size="sm"
              >
                Amendment {doc.amendmentStanding.ordinal} of {doc.amendmentStanding.limit} allowed
              </Badge>
            )}
          </div>

          <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 [font-size:13px] text-[var(--pv-muted)]">
            <span className="pv-mono">{doc.senderReference}</span>
            <span aria-hidden>·</span>
            <span>{doc.documentTypeName}</span>
            {doc.destinationCountry && (
              <>
                <span aria-hidden>·</span>
                <span>to {country(doc.destinationCountry)}</span>
              </>
            )}
            <span aria-hidden>·</span>
            <span>received {relative(doc.receivedAt)}</span>
          </p>
          <div className="mt-2">
            <IntakeChannelBadge channel={doc.intakeChannel} identity={doc.intakeIdentity} />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <SendAgain doc={doc} stamped={!awaitingStamp} />
          {isSupport && actionable && (
            <span className="[font-size:12.5px] text-[var(--pv-muted)]">
              Decisions on this request belong to the chamber&rsquo;s staff.
            </span>
          )}
          {actionable && !isSupport && (
            <>
              <Button
                variant="secondary"
                leadingIcon={<MessageSquareWarning className="size-3.5" />}
                onClick={() => setClarifyOpen(true)}
              >
                Request clarification
              </Button>
              <Button
                variant="secondary"
                leadingIcon={<X className="size-3.5" />}
                onClick={() => setRejectOpen(true)}
              >
                Reject
              </Button>
              <Button
                variant="primary"
                leadingIcon={<Check className="size-3.5" />}
                onClick={() => setApproveOpen(true)}
                disabled={(doc.approvalContext?.blockers.length ?? 0) > 0 || doc.organisationActive === false}
              >
                Approve
              </Button>
            </>
          )}
          {issued && (
            <>
              {/* When stamping has not produced a PDF, this link still resolves — to the
                  UNSTAMPED submission. Offering that as "Download" beside an Issued badge
                  is the same lie the callout above exists to stop, so it says what it is. */}
              <Button variant="secondary" asChild leadingIcon={<Download className="size-3.5" />}>
                <a href={doc.documentFileUrl ?? '#'} download>
                  {awaitingStamp ? 'Download submission' : 'Download'}
                </a>
              </Button>
              {!isSupport && (
                <>
                  <Button
                    variant="secondary"
                    leadingIcon={<ScrollText className="size-3.5" />}
                    onClick={() => setModifyOpen(true)}
                  >
                    Raise a correction
                  </Button>
                  <Button
                    variant="secondary"
                    leadingIcon={<Ban className="size-3.5" />}
                    onClick={() => setRevokeOpen(true)}
                  >
                    Revoke
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* A certificate approved but not produced. Given its own block above the number,
          because the number has already been drawn and §2.2 says it can never be reused —
          this is the most consequential thing on the screen when it is true. */}
      {doc.stamping && (
        <Callout
          tone="stop"
          icon={<AlertTriangle className="size-4" />}
          title={
            doc.stamping.deterministic
              ? 'This certificate could not be stamped, and retrying will not change that'
              : 'This certificate could not be stamped'
          }
        >
          <p>{doc.stamping.error}</p>
          <p className="mt-2">
            {doc.stamping.deterministic
              ? 'Something about this document or the stamping configuration has to change first. The certificate number has been issued and cannot be reused.'
              : 'This looks temporary. Retrying may be all that is needed. The certificate number has been issued and cannot be reused.'}{' '}
            The exporter has not been told it was issued.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button
              variant="secondary"
              size="sm"
              loading={retryStamping.isPending}
              onClick={() => retryStamping.mutate()}
              leadingIcon={<RefreshCw className="size-3.5" />}
              disabled={isSupport}
            >
              {doc.stamping.deterministic ? 'Retry anyway' : 'Retry'}
            </Button>
            <span className="[font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
              {doc.stamping.attempts === 1
                ? 'Tried once.'
                : `Tried ${doc.stamping.attempts} times.`}
              {doc.stamping.code ? ` Reported as ${doc.stamping.code}.` : ''}
            </span>
          </div>
        </Callout>
      )}

      {/* The issued certificate's identity, given its own prominent block — this is what
          a person phoning the chamber will quote. */}
      {doc.certificateNo && (
        <Card className="flex flex-wrap items-center gap-x-8 gap-y-4 bg-[var(--pv-leaf-soft)] !border-[var(--pv-leaf)]">
          <div>
            <p className="pv-eyebrow text-[var(--pv-leaf-ink)]">Certificate number</p>
            <p className="pv-mono mt-1 [font-size:22px] font-semibold leading-none">{doc.certificateNo}</p>
          </div>
          <div>
            {/* The date ON the certificate, which for a back-dated one is not the day it
                was approved. Falls back to the approval instant for older rows. */}
            <p className="pv-eyebrow text-[var(--pv-leaf-ink)]">Certificate date</p>
            <p className="mt-1 [font-size:14px] font-medium leading-none">
              {doc.certificateDate ? auCalendarDate(doc.certificateDate) : auDate(doc.processedAt)}
            </p>
          </div>
          {doc.processedByName && (
            <div>
              <p className="pv-eyebrow text-[var(--pv-leaf-ink)]">By</p>
              <p className="mt-1 [font-size:14px] font-medium leading-none">{doc.processedByName}</p>
            </div>
          )}
          <div className="flex-1" />
          {/* Work in progress, not a failure. A Retry button beside "still being
              generated" reads as "something went wrong" — and for the two seconds this
              normally takes, nothing has. A real failure renders the stop callout above
              instead, and that one does offer a retry. */}
          {awaitingStamp && !doc.stamping && (
            <div className="flex items-center gap-2.5">
              <Loader2 aria-hidden className="size-4 animate-spin text-[var(--pv-leaf-ink)]" />
              <span className="[font-size:var(--pv-text-xs)] text-[var(--pv-leaf-ink)]">
                Stamping the certificate…
              </span>
            </div>
          )}

          {/* Finished. Said plainly, with the document one click away — the operator has
              just approved and this is the thing they were approving. */}
          {issued && !awaitingStamp && doc.documentFileUrl && (
            <div className="flex items-center gap-2.5">
              <Check aria-hidden className="size-4 text-[var(--pv-leaf-ink)]" />
              <span className="[font-size:var(--pv-text-xs)] text-[var(--pv-leaf-ink)]">
                Stamped and ready.
              </span>
              <Button
                variant="secondary"
                size="sm"
                asChild
                leadingIcon={<FileText className="size-3.5" />}
              >
                <a href={doc.documentFileUrl} target="_blank" rel="noreferrer">
                  Open certificate
                </a>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                loading={printing}
                leadingIcon={<Printer className="size-3.5" />}
                onClick={() => void print(doc.documentFileUrl!)}
              >
                Print
              </Button>
            </div>
          )}
        </Card>
      )}

      {doc.status === DocumentStatus.CLARIFICATION_REQUESTED && doc.clarification && (
        <Callout
          tone="warn"
          icon={<FileClock className="size-4" />}
          title={`Waiting on ${doc.organisationName} since ${auDate(doc.clarification.requestedAt)}`}
        >
          {doc.clarification.items.length > 0 && (
            <ul className="m-0 list-disc pl-4">
              {doc.clarification.items.map((item) => (
                <li key={item.code}>{item.label}</li>
              ))}
            </ul>
          )}
          {doc.clarification.freeText && <p className="mt-1.5">{doc.clarification.freeText}</p>}
          {doc.clarification.respondBy && (
            <p className="mt-2 [font-size:var(--pv-text-xs)]">
              Asked to respond by {auDate(doc.clarification.respondBy)}.
            </p>
          )}
        </Callout>
      )}

      {doc.rejectionReason && (
        <Callout tone="stop" icon={<X className="size-4" />} title={`Rejected — ${doc.rejectionReason.label}`}>
          {doc.rejectionReason.freeText}
          <p className="mt-2 [font-size:var(--pv-text-xs)]">
            {doc.processedByName} · {auDateTime(doc.processedAt)}
          </p>
        </Callout>
      )}

      {doc.revocation && (
        <Callout tone="stop" icon={<Ban className="size-4" />} title="This certificate has been revoked">
          {doc.revocation.reason}
          <p className="mt-2 [font-size:var(--pv-text-xs)]">{auDateTime(doc.revocation.revokedAt)}</p>
        </Callout>
      )}

      <div className="review-grid">
        <PdfViewer
          url={doc.documentFileUrl}
          filename={doc.documentFilename}
          pageCount={doc.pageCount}
        />

        <div className="flex min-w-0 flex-col gap-4">
          {actionable && doc.organisationActive === false && (
            <Callout
              tone="warn"
              icon={<AlertTriangle className="size-4" />}
              title={`${doc.organisationName} is an inactive exporter`}
            >
              No certificate can be issued for them until they are reactivated, so Approve is
              unavailable. The request waits here; nothing is lost.{' '}
              {doc.organisationId && (
                <Link to={`/exporters/${doc.organisationId}`} className="font-medium underline">
                  Open the exporter
                </Link>
              )}
              {' '}— a chamber admin can reactivate them there.
            </Callout>
          )}
          {actionable && (
            <CheckPanel
              checks={doc.checks}
              onRecheck={() => recheck.mutate()}
              rechecking={recheck.isPending}
              certificateOnly={doc.intakeChannel !== 'INTEGRATOR'}
            />
          )}

          <Tabs defaultValue="data">
            <TabsList>
              {/* The label has to be honest about which it is. On upload and email nothing
                  was submitted — these are details a machine read off the page, and calling
                  them "submitted" would overstate what the chamber is looking at. */}
              <TabsTrigger value="data">
                {doc.extraction.provenance === 'EXTRACTED' ? 'Read from the certificate' : 'Submitted data'}
              </TabsTrigger>
              <TabsTrigger value="attachments" count={doc.supportingDocuments.length}>
                Attachments
              </TabsTrigger>
              <TabsTrigger value="history" count={doc.audit.length}>
                History
              </TabsTrigger>
              {!actionable && <TabsTrigger value="checks">Checks</TabsTrigger>}
            </TabsList>

            <TabsContent value="data" className="pt-4">
              <div className="flex flex-col gap-4">
                {doc.extraction.provenance === 'EXTRACTED' ? (
                  <ExtractionPanel
                    panel={doc.extraction}
                    onUseSuggestedType={
                      // Only while it can still be changed. After issue the number was
                      // drawn from the current type and changing it would leave a number
                      // that no longer means what it says.
                      doc.certificateNo || isSupport ? undefined : (id) => changeType.mutate(id)
                    }
                  />
                ) : (
                  <ShipmentPanel data={doc.structuredData} />
                )}
                <SignatureComparison comparison={doc.signatureComparison} />
              </div>
            </TabsContent>

            <TabsContent value="attachments" className="pt-4">
              <AttachmentsPanel
                documentId={doc.id}
                attachments={doc.supportingDocuments}
                // Matches what the API allows, which is wider than `actionable`: a request
                // awaiting clarification is still open, and attaching the document the
                // exporter was asked for is exactly what an operator does there. Hiding the
                // controls anywhere the API would accept them means offering less than the
                // product can do; showing them where it would refuse means offering a
                // button that fails.
                editable={
                  doc.status === DocumentStatus.AWAITING_ACTION ||
                  doc.status === DocumentStatus.CLARIFICATION_REQUESTED
                }
              />
            </TabsContent>

            <TabsContent value="history" className="flex flex-col gap-4 pt-4">
              {doc.emails.length > 0 && (
                <Card>
                  <CardHeader
                    title="Emails"
                    description="Everything sent about this request, and whether it arrived."
                  />
                  <ul className="flex list-none flex-col gap-0 p-0">
                    {doc.emails.map((email) => (
                      <li key={email.id} className="flex items-start gap-3 border-b border-[var(--pv-line)] py-2.5 last:border-b-0">
                        <div className="min-w-0 flex-1">
                          <p className="truncate [font-size:13px] font-medium">{email.subject}</p>
                          <p className="truncate [font-size:12px] text-[var(--pv-faint)]">
                            To {email.recipient} · {auDateTime(email.sentAt ?? email.createdAt)}
                          </p>
                          {email.deliveryDetail && (
                            <p className="mt-0.5 [font-size:12px] text-[var(--pv-muted)]">{email.deliveryDetail}</p>
                          )}
                        </div>
                        <EmailStatus status={email.status} />
                      </li>
                    ))}
                  </ul>
                </Card>
              )}
              <Card>
                <CardHeader
                  title="Everything that happened"
                  description="Append-only. Every state change records who and when."
                />
                <ol className="flex list-none flex-col gap-0 p-0">
                  {doc.audit.map((event) => (
                    <li
                      key={event.id}
                      className="grid grid-cols-[auto_minmax(0,1fr)] gap-3 border-b border-[var(--pv-line)] py-3 last:border-b-0 last:pb-0"
                    >
                      <span
                        aria-hidden
                        className="mt-1.5 size-1.5 rounded-full bg-[var(--pv-leaf-deep)]"
                      />
                      <div className="min-w-0">
                        <p className="[font-size:13px] leading-snug">{event.summary}</p>
                        <p className="mt-0.5 flex flex-wrap items-center gap-2 [font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
                          <span>{event.actorName ?? 'System'}</span>
                          <span aria-hidden>·</span>
                          <span>{auDateTime(event.createdAt)}</span>
                          <CodeChip>{event.action}</CodeChip>
                        </p>
                        {/* Acknowledged flags are surfaced explicitly: an approval made
                            past a failed check should be visible in the history, not
                            buried in a JSON blob. */}
                        {Array.isArray(event.detail?.['acknowledgedFlags']) &&
                          (event.detail!['acknowledgedFlags'] as string[]).length > 0 && (
                            <p className="mt-1.5 [font-size:var(--pv-text-2xs)] text-[var(--pv-warn)]">
                              Approved despite:{' '}
                              {(event.detail!['acknowledgedFlags'] as string[]).join(', ')}
                            </p>
                          )}
                        {typeof event.detail?.['operatorNote'] === 'string' &&
                          event.detail!['operatorNote'] && (
                            <p className="mt-1.5 border-l-2 border-[var(--pv-line-strong)] pl-2 [font-size:var(--pv-text-xs)] italic text-[var(--pv-ink-soft)]">
                              {event.detail!['operatorNote'] as string}
                            </p>
                          )}
                      </div>
                    </li>
                  ))}
                </ol>
              </Card>
            </TabsContent>

            {!actionable && (
              <TabsContent value="checks" className="pt-4">
                <CheckPanel
                  checks={doc.checks}
                  onRecheck={() => recheck.mutate()}
                  rechecking={recheck.isPending}
                  certificateOnly={doc.intakeChannel !== 'INTEGRATOR'}
                />
              </TabsContent>
            )}
          </Tabs>

          <Card>
            <CardHeader title="This request" />
            <dl className="m-0">
              <KeyValue label="Reference" mono>
                {doc.senderReference}
              </KeyValue>
              <KeyValue label="Document type">
                {doc.documentTypeName} <CodeChip>{doc.documentTypeCode}</CodeChip>
              </KeyValue>
              <KeyValue label="Submission">
                {doc.submissionType === 'AMENDMENT' ? 'Amendment' : 'Original'}
                {doc.messageType === 'REISSUE' && ' (reissue)'}
              </KeyValue>
              <KeyValue label="Delivery">{doc.deliveryMode.toLowerCase()}</KeyValue>
              <KeyValue label="Received">{auDateTime(doc.receivedAt)}</KeyValue>
              <KeyValue label="Required by">
                {doc.requestedTargetDate ? auDate(doc.requestedTargetDate) : 'No date supplied'}
              </KeyValue>
              <KeyValue label="File">
                <span className="inline-flex items-center gap-1.5">
                  <FileText aria-hidden className="size-3.5 text-[var(--pv-faint)]" />
                  {doc.documentFilename}
                </span>
              </KeyValue>
              {doc.amendments.length > 0 && (
                <KeyValue label="Amended by">
                  {doc.amendments.map((amendment) => (
                    <Link
                      key={amendment.id}
                      to={`/documents/${amendment.id}`}
                      className="pv-mono text-[var(--pv-leaf-deep)] underline decoration-dotted"
                    >
                      {amendment.certificateNo ?? 'pending'}
                    </Link>
                  ))}
                </KeyValue>
              )}
            </dl>
          </Card>
        </div>
      </div>

      {actionable && (
        <>
          <ApproveDialog document={doc} open={approveOpen} onOpenChange={setApproveOpen} />
          <RejectDialog document={doc} open={rejectOpen} onOpenChange={setRejectOpen} />
          <ClarificationDialog document={doc} open={clarifyOpen} onOpenChange={setClarifyOpen} />
        </>
      )}
      {issued && (
        <>
          <RevokeDialog document={doc} open={revokeOpen} onOpenChange={setRevokeOpen} />
          <RaiseModificationDialog
            documentId={doc.id}
            certificateNo={doc.certificateNo ?? ''}
            open={modifyOpen}
            onOpenChange={setModifyOpen}
          />
        </>
      )}
    </div>
  );
}

/** What became of an email (T-149). "Sent" until Amazon SES reports delivery. */
function EmailStatus({ status }: { status: string }) {
  const map: Record<string, { label: string; tone: 'ok' | 'warn' | 'stop' | 'neutral' | 'info' }> = {
    DELIVERED: { label: 'Delivered', tone: 'ok' },
    SENT: { label: 'Sent', tone: 'neutral' },
    BOUNCED: { label: 'Bounced', tone: 'stop' },
    COMPLAINED: { label: 'Marked as spam', tone: 'stop' },
    FAILED: { label: 'Failed', tone: 'stop' },
    NOT_SENT: { label: 'Not sent', tone: 'warn' },
    PENDING: { label: 'Sending', tone: 'info' },
  };
  const s = map[status] ?? { label: status, tone: 'neutral' as const };
  return <Badge tone={s.tone} size="sm">{s.label}</Badge>;
}
